import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import storageManager from '../utils/storageManager.js';

function StorageIndicator({ compact = false }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStorageStats();
  }, []);

  const loadStorageStats = async () => {
    try {
      const storageStats = await storageManager.getStorageStats();
      setStats(storageStats);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load storage stats:', error);
      setLoading(false);
    }
  };

  const getStorageLevel = (percentage) => {
    if (percentage < 60) return 'good';
    if (percentage < 80) return 'warning';
    return 'critical';
  };

  const getStorageColor = (percentage) => {
    if (percentage < 60) return '#27AE60';
    if (percentage < 80) return '#FFA500';
    return '#E74C3C';
  };

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '16px 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '13px', color: '#8B7355' }}>
          {t('storage.loadingInfo')}
        </div>
      </div>
    );
  }

  if (!stats || stats.quota === 0) {
    return null; // Don't show if stats unavailable
  }

  const level = getStorageLevel(stats.percentage);
  const color = getStorageColor(stats.percentage);

  // Compact version (for cards/small spaces)
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '11px',
        color: '#8B7355'
      }}>
        <span>💾</span>
        <div style={{ flex: 1, maxWidth: '60px' }}>
          <div style={{
            height: '4px',
            background: 'rgba(184, 160, 130, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${Math.min(stats.percentage, 100)}%`,
              background: color,
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
        <span>{stats.usageMB} MB</span>
      </div>
    );
  }

  // Full version (for settings/main view)
  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <span style={{
          fontSize: '13px',
          color: '#8B7355',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          💾 {t('storage.title')}
        </span>
        <span style={{
          fontSize: '13px',
          color: '#B8A082',
          fontWeight: '700'
        }}>
          {stats.usageMB.toFixed(1)} MB / {(stats.quotaMB / 1024).toFixed(1)} GB
        </span>
      </div>

      {/* Progress Bar */}
      <div style={{
        height: '10px',
        background: 'rgba(184, 160, 130, 0.2)',
        borderRadius: '10px',
        overflow: 'hidden',
        marginBottom: '10px'
      }}>
        <div style={{
          height: '100%',
          width: `${Math.min(stats.percentage, 100)}%`,
          background: color,
          borderRadius: '10px',
          transition: 'width 0.3s ease'
        }}></div>
      </div>

      {/* Info Text */}
      <div style={{
        fontSize: '11px',
        color: '#666',
        lineHeight: '1.4'
      }}>
        {level === 'good' && (
          <span>✅ {t('storage.availableSpace', { total: stats.quotaGB.toFixed(1) })}</span>
        )}
        {level === 'warning' && (
          <span>⚠️ {t('storage.limitedSpace')}</span>
        )}
        {level === 'critical' && (
          <span style={{ color: '#E74C3C', fontWeight: '600' }}>
            🚨 {t('storage.criticalStorage')}
          </span>
        )}
      </div>
    </div>
  );
}

export default StorageIndicator;
